import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormFieldText } from 'Src/feature/form/FormFieldText';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { useToast } from 'Src/feature/toasts/useToast';

import { useCheck } from './useCheck';

const sectionId = 'TaxExemptModalContent';

interface TaxExemptModalContentProps {
  checkId: number | undefined;
  open: boolean;
  onClose: () => void;
}

const TaxExemptModalContent: FC<TaxExemptModalContentProps> = ({ checkId, open, onClose }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [taxId, setTaxId] = useState('');
  const getTaxIdQuery = useCheck.query.getTaxId(checkId);
  const setTaxIdMutation = useCheck.mutate.setTaxId();
  const existingTaxId = getTaxIdQuery.data?.taxId ?? '';
  const hasChanges = taxId.trim() !== existingTaxId;

  useEffect(() => {
    if (open) {
      setTaxId(getTaxIdQuery.data?.taxId ?? '');
    }
  }, [open, getTaxIdQuery.data?.taxId]);

  const onDone = () => {
    if (typeof checkId !== 'number') return;
    setTaxIdMutation.mutate({ checkId, taxId }, {
      onSuccess: () => {
        onClose();
      },
      onError: (error) => {
        toast.show.serviceError(error, { id: `${sectionId}.setTaxId` });
      },
    });
  };

  const onRemove = () => {
    if (typeof checkId !== 'number') return;
    setTaxIdMutation.mutate({ checkId, taxId: undefined }, {
      onSuccess: () => {
        onClose();
      },
      onError: (error) => {
        toast.show.serviceError(error, { id: `${sectionId}.setTaxId` });
      },
    });
  };

  return (
    <>
      <ModalHeader>
        <ModalTitle copy={t('labels.taxExempt')} />
      </ModalHeader>

      <ModalContent>
        <FormFieldText
          type="text"
          variant="underline"
          value={taxId}
          onChange={(e) => setTaxId(e.target.value)}
          placeholder={t('taxExempt.placeholder')}
        />
      </ModalContent>

      <ModalActions>
        {getTaxIdQuery.data?.taxId && (
          <ModalAction
            label={t('labels.remove')}
            onClick={onRemove}
            color="error"
            disabled={setTaxIdMutation.isPending}
          />
        )}
        <ModalAction
          label={t('labels.cancel')}
          onClick={onClose}
          color="secondary"
        />
        <ModalAction
          label={t('labels.done')}
          onClick={onDone}
          color="primary"
          disabled={setTaxIdMutation.isPending || !taxId.trim() || !hasChanges}
        />
      </ModalActions>
    </>
  );
};

export { TaxExemptModalContent };
export type { TaxExemptModalContentProps };
